import { Box, CircularProgress, Paper } from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { AdminUser } from '@/types/domain';

export interface AdminUserTableProps {
  users: AdminUser[];
  loading: boolean;
  onEdit: (user: AdminUser) => void;
  onDelete: (id: number) => void;
}

export function AdminUserTable({ users, loading, onEdit, onDelete }: AdminUserTableProps) {
  const { t } = useTranslation();

  const columns: GridColDef<AdminUser>[] = [
    { field: 'id', headerName: t('users.id'), width: 70 },
    { field: 'username', headerName: t('users.username'), width: 150 },
    {
      field: 'email',
      headerName: t('users.email'),
      width: 200,
      valueGetter: (_value: unknown, row: AdminUser) => row.email || t('users.notAvailable'),
    },
    {
      field: 'created_at',
      headerName: t('users.createdAt'),
      width: 180,
      renderCell: (params: GridRenderCellParams<AdminUser>) => {
        try {
          const value = params.row?.created_at;
          if (!value) return '';
          const date = new Date(value.includes(' ') ? value.replace(' ', 'T') : value);
          if (isNaN(date.getTime())) return t('users.invalidDate');
          return format(date, 'yyyy-MM-dd HH:mm:ss');
        } catch {
          return t('users.invalidDate');
        }
      },
    },
    {
      field: 'last_login',
      headerName: t('users.lastLogin'),
      width: 180,
      renderCell: (params: GridRenderCellParams<AdminUser>) => {
        try {
          const value = params.row?.last_login;
          if (!value) return t('users.never');
          const date = new Date(value.includes(' ') ? value.replace(' ', 'T') : value);
          if (isNaN(date.getTime())) return t('users.invalidDate');
          const diff = Date.now() - date.getTime();
          const formatted = format(date, 'yyyy-MM-dd HH:mm:ss');
          if (diff < 5 * 60 * 1000) return formatted + ' (' + t('users.online') + ')';
          return formatted;
        } catch {
          return t('users.invalidDate');
        }
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('users.actions'),
      width: 120,
      getActions: (params: GridRowParams<AdminUser>) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label={t('users.edit')}
          onClick={() => params.row && onEdit(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label={t('users.delete')}
          onClick={() => params.row?.id && onDelete(params.row.id)}
        />,
      ],
    },
  ];

  return (
    <Paper>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={users.map((u) => ({ ...u, id: u.id }))}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      )}
    </Paper>
  );
}
